import { useEffect, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ImageUpload } from "@/components/image-upload";
import { VideoUpload } from "@/components/video-upload";
import { Plus, Trash2, Play, ImageIcon, Video } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/gallery")({
  head: () => ({ meta: [{ title: "Galeri — MI Al-Munajah Admin" }] }),
  component: GalleryPage,
});

type Cat = "kegiatan" | "event" | "dokumentasi";
type MediaT = "foto" | "video";
type Item = {
  id: string; title: string; description: string | null;
  category: Cat; media_type: MediaT; media_url: string;
  thumbnail_url: string | null;
};

const empty = {
  title: "", description: "",
  category: "kegiatan" as Cat, media_type: "foto" as MediaT,
  media_url: null as string | null,
  thumbnail_url: null as string | null,
};

function GalleryPage() {
  const [rows, setRows] = useCachedState<Item[]>("gallery:rows", []);
  const [mode, setMode] = useState<MediaT>("foto");
  const [tab, setTab] = useState<"all" | Cat>("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty });
  const [previewItem, setPreviewItem] = useState<Item | null>(null);

  const load = async () => {
    const { data } = await supabase.from("gallery").select("*").order("created_at", { ascending: false });
    setRows((data || []) as Item[]);
  };
  useEffect(() => {
    load();
    const ch = supabase.channel("gal-rt").on("postgres_changes", { event: "*", schema: "public", table: "gallery" }, load).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const save = async () => {
    if (!form.title || !form.media_url) return toast.error("Judul & media wajib diisi");
    const { error } = await supabase.from("gallery").insert({
      title: form.title, description: form.description, category: form.category,
      media_type: form.media_type, media_url: form.media_url,
      thumbnail_url: form.thumbnail_url,
    });
    if (error) return toast.error(error.message);
    toast.success("Media ditambahkan"); setOpen(false); setForm(empty);
  };
  const remove = async (id: string) => {
    const { error } = await supabase.from("gallery").delete().eq("id", id);
    if (error) toast.error(error.message); else toast.success("Media dihapus");
  };

  const filtered = rows.filter((r) => r.media_type === mode && (tab === "all" || r.category === tab));
  const fotoCount = rows.filter((r) => r.media_type === "foto").length;
  const videoCount = rows.filter((r) => r.media_type === "video").length;

  const openAdd = (m: MediaT) => {
    setForm({ ...empty, media_type: m });
    setOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Galeri"
        description={`${fotoCount} foto · ${videoCount} video`}
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild><Button className="rounded-xl" onClick={() => openAdd(mode)}><Plus className="w-4 h-4 mr-2" /> Tambah {mode === "foto" ? "Foto" : "Video"}</Button></DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle>Tambah Media</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5"><Label>Judul *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Deskripsi</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Kategori</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v as Cat })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kegiatan">Kegiatan Sekolah</SelectItem>
                        <SelectItem value="event">Event</SelectItem>
                        <SelectItem value="dokumentasi">Dokumentasi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Jenis Media</Label>
                    <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v as MediaT, media_url: null })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="foto">Foto</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>{form.media_type === "foto" ? "Foto" : "Video"} *</Label>
                  {form.media_type === "foto" ? (
                    <ImageUpload bucket="gallery" accept="image/*"
                      value={form.media_url} onChange={(url) => setForm({ ...form, media_url: url, thumbnail_url: null })}
                      label="Upload foto" />
                  ) : (
                    <VideoUpload
                      videoUrl={form.media_url} thumbnailUrl={form.thumbnail_url}
                      onChange={({ videoUrl, thumbnailUrl }) => setForm({ ...form, media_url: videoUrl, thumbnail_url: thumbnailUrl })}
                      label="Upload video" />
                  )}
                  {form.media_type === "video" && form.media_url && (
                    <p className="text-[11px] text-muted-foreground">Thumbnail dibuat otomatis dari frame video.</p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
                <Button onClick={save} className="rounded-xl">Tambah</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Tabs value={mode} onValueChange={(v) => setMode(v as MediaT)} className="mb-3">
        <TabsList className="grid grid-cols-2 w-full sm:w-80">
          <TabsTrigger value="foto"><ImageIcon className="w-4 h-4 mr-2" /> Foto ({fotoCount})</TabsTrigger>
          <TabsTrigger value="video"><Video className="w-4 h-4 mr-2" /> Video ({videoCount})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "all" | Cat)} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">Semua</TabsTrigger>
          <TabsTrigger value="kegiatan">Kegiatan</TabsTrigger>
          <TabsTrigger value="event">Event</TabsTrigger>
          <TabsTrigger value="dokumentasi">Dokumentasi</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-16">Belum ada {mode === "foto" ? "foto" : "video"}</div>}
        {filtered.map((m) => (
          <Card key={m.id} className="rounded-2xl overflow-hidden group relative">
            <div className="aspect-square bg-muted relative cursor-pointer" onClick={() => setPreviewItem(m)}>
              {m.media_type === "foto" ? (
                <img src={m.media_url} alt={m.title} className="w-full h-full object-cover" />
              ) : (
                <>
                  {m.thumbnail_url ? (
                    <img src={m.thumbnail_url} alt={m.title} className="w-full h-full object-cover" />
                  ) : (
                    <video src={m.media_url} className="w-full h-full object-cover" preload="metadata" muted playsInline />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                    <Play className="w-10 h-10 text-white" fill="currentColor" />
                  </div>
                </>
              )}
              <Badge className="absolute top-2 left-2 rounded-full bg-black/50 text-white border-0 capitalize text-[10px]">{m.category}</Badge>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="icon" className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" onClick={(e) => e.stopPropagation()}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Hapus media?</AlertDialogTitle><AlertDialogDescription>Tindakan tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(m.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div className="p-2.5">
              <div className="text-xs font-medium truncate">{m.title}</div>
              {m.description && <div className="text-[10px] text-muted-foreground truncate">{m.description}</div>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!previewItem} onOpenChange={(v) => { if (!v) setPreviewItem(null); }}>
        <DialogContent className="max-w-3xl rounded-3xl p-0 overflow-hidden">
          <div className="relative bg-black">
            {previewItem?.media_type === "foto" ? (
              <img src={previewItem.media_url} alt={previewItem.title} className="w-full max-h-[70vh] object-contain mx-auto" />
            ) : (
              <video src={previewItem?.media_url} controls className="w-full max-h-[70vh] mx-auto" preload="metadata" playsInline />
            )}
          </div>
          <div className="p-4">
            <div className="font-medium text-sm">{previewItem?.title}</div>
            {previewItem?.description && <div className="text-xs text-muted-foreground mt-1">{previewItem.description}</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
